import { useState } from "react";
import { InputLabel, InputContainer } from "@sellar-io/components";
import { Alert } from "../../../components/alert/Alert";
import { Button, InputSelect } from "@sellar-io/components";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
} from "../../../components/modal/Dialog";
import {
  FulfillmentTypeGetStatsResponseType,
  FulfillmentTypeListResponseType,
} from "@sellar-io/api-v2/types";
import { useForm } from "@tanstack/react-form";
import { apiV2AssertError } from "../../../hooks/api/v2/apiV2Request";
import { useFulfillmentTypeGetStats } from "../../../hooks/api/v2/fulfillmentType/useFulfillmentTypeGetStats";
import { useFulfillmentTypeDeleteAndReassign } from "../../../hooks/api/v2/fulfillmentType/useFulfillmentTypeDeleteAndReassign";
import { useFulfillmentTypeList } from "../../../hooks/api/v2/fulfillmentType/useFulfillmentTypeList";
import { capitalize } from "@sellar-io/common";
import { z } from "zod";

type DeliveryMethodDeleteDialogProps = {
  fulfillmentTypeId: number;
  fulfillmentTypeName: string;
  fulfillmentTypeType: "direct" | "courier" | "haulier";
  active: boolean;
  setActive: (active: boolean) => void;
};

export const DeliveryMethodDeleteDialog = ({
  fulfillmentTypeId,
  fulfillmentTypeName,
  fulfillmentTypeType,
  active,
  setActive,
}: DeliveryMethodDeleteDialogProps) => {
  const fulfillmentTypeStatsQuery = useFulfillmentTypeGetStats(
    fulfillmentTypeId,
    {
      enabled: Boolean(fulfillmentTypeId),
    },
  );

  const customersDirectlyAssignedCount =
    fulfillmentTypeStatsQuery.data?.SupplierRetailerConnections.length ?? 0;
  const customerZonesAssigned =
    fulfillmentTypeStatsQuery.data?.customerZonesAssigned ?? [];

  const hasEntitiesToReassign =
    customersDirectlyAssignedCount > 0 || customerZonesAssigned.length > 0;

  const loading = fulfillmentTypeStatsQuery.isLoading;

  const description = hasEntitiesToReassign
    ? "To delete this delivery method, you will need to re-assign a different delivery method to the individual customers and/or customer zones that currently have it assigned"
    : `Are you sure you want to delete ${fulfillmentTypeName} delivery method`;

  return (
    <>
      <Dialog active={active} onDismiss={() => setActive(false)}>
        <DialogNavigation
          leftIcon="CHEVRON_DOWN"
          onClickLeftIcon={() => setActive(false)}
          title={`Delete ${fulfillmentTypeName}?`}
          description={description}
        />
        <DialogContent>
          {active && !loading && (
            <Content
              fulfillmentTypeId={fulfillmentTypeId}
              fulfillmentTypeType={fulfillmentTypeType}
              setActive={setActive}
              hasEntitiesToReassign={hasEntitiesToReassign}
              customersDirectlyAssignedCount={customersDirectlyAssignedCount}
              customerZonesAssigned={customerZonesAssigned}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

const Content = ({
  fulfillmentTypeId,
  fulfillmentTypeType,
  setActive,
  hasEntitiesToReassign,
  customersDirectlyAssignedCount,
  customerZonesAssigned,
}: {
  fulfillmentTypeId: number;
  fulfillmentTypeType: "direct" | "courier" | "haulier";
  setActive: (active: boolean) => void;
  hasEntitiesToReassign: boolean;
  customersDirectlyAssignedCount: number;
  customerZonesAssigned: {
    id: number;
    name: string | null;
    customersAssignedCount: number;
  }[];
}) => {
  const [submissionErrorMessage, setSubmissionErrorMessage] = useState("");
  const fulfillmentTypeDeleteAndReassignMutation =
    useFulfillmentTypeDeleteAndReassign(fulfillmentTypeId);

  const fulfillmentTypeListQuery = useFulfillmentTypeList(
    {},
    {
      enabled: Boolean(fulfillmentTypeId),
    },
  );

  const handleDeleteAndReassign = (
    data: FulfillmentTypeDeleteAndReassignFormValues,
  ) => {
    fulfillmentTypeDeleteAndReassignMutation.mutate(
      {
        customerZones: data.customerZones.map((customerZone) => ({
          id: customerZone.customerZoneId,
          newFulfillmentTypeId: customerZone.newFulfillmentTypeId,
          type: fulfillmentTypeType,
        })),
        directlyAssignedCustomers:
          data.newFulfillmentTypeIdForDirectlyAssignedCustomers !== 0
            ? {
                oldFulfillmentTypeId: fulfillmentTypeId,
                newFulfillmentTypeId:
                  data.newFulfillmentTypeIdForDirectlyAssignedCustomers,
                type: fulfillmentTypeType,
              }
            : null,
      },
      {
        onSuccess: () => {
          setActive(false);
        },
        onError: (error) => {
          const message = apiV2AssertError(error)?.response?.data.message ?? "";
          setSubmissionErrorMessage(message);
        },
      },
    );
  };

  const fulfillmentTypeList = fulfillmentTypeListQuery.data?.pages[0] ?? [];

  const isSubmitting = false;

  return (
    <>
      {hasEntitiesToReassign ? (
        <FulfillmentTypeDeleteAndReassignForm
          customerZonesAssigned={customerZonesAssigned}
          customersDirectlyAssignedCount={customersDirectlyAssignedCount}
          fulfillmentTypeList={fulfillmentTypeList}
          currentFulfillmentTypeId={fulfillmentTypeId}
          currentFulfillmentTypeType={fulfillmentTypeType}
          isSubmitting={isSubmitting}
          errorMessage={submissionErrorMessage}
          onSubmit={handleDeleteAndReassign}
        />
      ) : (
        <div className="flex justify-end">
          <Button
            color="red"
            disabled={isSubmitting}
            loading={isSubmitting}
            onClick={() => {
              handleDeleteAndReassign({
                customerZones: [],
                newFulfillmentTypeIdForDirectlyAssignedCustomers: null,
              });
            }}
          >
            Delete
          </Button>
        </div>
      )}
    </>
  );
};

type FulfillmentTypeDeleteAndReassignFormProps = {
  fulfillmentTypeList: FulfillmentTypeListResponseType;
  currentFulfillmentTypeId: number;
  currentFulfillmentTypeType: "direct" | "courier" | "haulier";
  customersDirectlyAssignedCount: number;
  customerZonesAssigned: FulfillmentTypeGetStatsResponseType["customerZonesAssigned"];
  isSubmitting: boolean;
  errorMessage: string;
  onSubmit: (data: FulfillmentTypeDeleteAndReassignFormValues) => void;
};

const formSchema = z.object({
  customerZones: z
    .object({
      customerZoneId: z.number().min(1),
      newFulfillmentTypeId: z.number().min(1),
    })
    .array(),
  newFulfillmentTypeIdForDirectlyAssignedCustomers: z
    .number()
    .min(1)
    .nullable(),
});

type FulfillmentTypeDeleteAndReassignFormValues = z.infer<typeof formSchema>;

const FulfillmentTypeDeleteAndReassignForm = ({
  fulfillmentTypeList,
  currentFulfillmentTypeId,
  currentFulfillmentTypeType,
  customersDirectlyAssignedCount,
  customerZonesAssigned,
  isSubmitting,
  errorMessage,
  onSubmit,
}: FulfillmentTypeDeleteAndReassignFormProps) => {
  const form = useForm({
    defaultValues: {
      customerZones: customerZonesAssigned.map((cz) => ({
        customerZoneId: cz.id,
        newFulfillmentTypeId: 0,
      })),
      newFulfillmentTypeIdForDirectlyAssignedCustomers: 0,
    },
    onSubmit: ({ value }) => {
      onSubmit(
        formSchema.parse({
          ...value,
          newFulfillmentTypeIdForDirectlyAssignedCustomers:
            value.newFulfillmentTypeIdForDirectlyAssignedCustomers !== 0
              ? value.newFulfillmentTypeIdForDirectlyAssignedCustomers
              : null,
        }),
      );
    },
  });

  const filteredFulfillmentTypeList = fulfillmentTypeList
    .filter(
      (fulfillmentType) => fulfillmentType.id !== currentFulfillmentTypeId,
    )
    .filter(
      (fulfillmentType) =>
        fulfillmentType.fulfillmentType === currentFulfillmentTypeType,
    );

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        form.handleSubmit();
      }}
      className="flex flex-col gap-4"
    >
      {errorMessage && (
        <Alert type="error" message={errorMessage} className="w-full" />
      )}
      <div className="flex flex-col gap-4">
        {customerZonesAssigned.map((customerZoneAssigned, idx) => {
          const customerZoneName =
            customerZoneAssigned.name ??
            `Customer Zone - ${customerZoneAssigned.id}`;

          return (
            <div key={customerZoneAssigned.id} className="flex flex-col gap-2">
              <form.Field name={`customerZones[${idx}]`}>
                {({ state, handleBlur, handleChange }) => (
                  <>
                    <InputLabel>
                      {`Select delivery method for your '${customerZoneName}' customer zone`}
                    </InputLabel>

                    <InputContainer>
                      <InputSelect
                        value={state.value.newFulfillmentTypeId}
                        onBlur={handleBlur}
                        onChange={(event) =>
                          handleChange({
                            customerZoneId: state.value.customerZoneId,
                            newFulfillmentTypeId: Number(event.target.value),
                          })
                        }
                      >
                        <option value="">Select a delivery method…</option>
                        {filteredFulfillmentTypeList.map((type) => (
                          <option key={type.id} value={type.id}>
                            {type.name ?? capitalize(type.fulfillmentType)}
                          </option>
                        ))}
                      </InputSelect>
                    </InputContainer>
                  </>
                )}
              </form.Field>
            </div>
          );
        })}
        {customersDirectlyAssignedCount > 0 && (
          <div className="flex flex-col gap-2">
            <form.Field name="newFulfillmentTypeIdForDirectlyAssignedCustomers">
              {({ state, handleBlur, handleChange }) => (
                <>
                  <InputLabel>{`Select delivery method for ${customersDirectlyAssignedCount} individually assigned customers`}</InputLabel>
                  <InputContainer>
                    <InputSelect
                      value={state.value}
                      onBlur={handleBlur}
                      onChange={(event) =>
                        handleChange(Number(event.target.value))
                      }
                    >
                      <option value="">Select a delivery method…</option>
                      {filteredFulfillmentTypeList.map((type) => (
                        <option key={type.id} value={type.id}>
                          {type.name ?? capitalize(type.fulfillmentType)}
                        </option>
                      ))}
                    </InputSelect>
                  </InputContainer>
                </>
              )}
            </form.Field>
          </div>
        )}
      </div>
      <div className="flex justify-end">
        <Button disabled={isSubmitting} loading={isSubmitting} type="submit">
          Delete and Assign
        </Button>
      </div>
    </form>
  );
};
